import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Input, Button, Table, ConfirmModal } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import {
  findWarrantySettings,
  updateWarrantySettings,
  findWarrantyPeriods,
  createWarrantyPeriod,
  updateWarrantyPeriod,
  deleteWarrantyPeriod,
} from '../actions';
import AddPeriodModal from './AddPeriodModal';

const WarrantySettings = memo(
  ({
    auth: { companyId },
    warrantySettingsState: { warrantySettingsData, warrantyPeriodsData, isLoading },
    showAlert,
    findWarrantySettings,
    updateWarrantySettings,
    findWarrantyPeriods,
    createWarrantyPeriod,
    updateWarrantyPeriod,
    deleteWarrantyPeriod,
  }) => {
    const originalDataRef = useRef({});
    const [warrantySettingsForm, setWarrantySettingsForm] = useState({
      'days-in-warranty-year': '',
      'days-in-warranty-month': '',
      'days-in-warranty-week': '',
    });
    const [isAddPeriodModalOpen, setAddPeriodModalOpen] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [periodIdToDelete, setPeriodIdToDelete] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
      findWarrantySettings();
      findWarrantyPeriods();
    }, []);

    useEffect(() => {
      if (
        Array.isArray(warrantySettingsData) &&
        warrantySettingsData.length > 0
      ) {
        const nextForm = {
          'days-in-warranty-year': '',
          'days-in-warranty-month': '',
          'days-in-warranty-week': '',
        };
        for (const item of warrantySettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
        originalDataRef.current = { ...nextForm };
        setWarrantySettingsForm(nextForm);
      }
    }, [warrantySettingsData]);

    const handleChangeInputValues = (e) => {
      const { id, value } = e.target;
      if (id && Object.prototype.hasOwnProperty.call(warrantySettingsForm, id)) {
        setWarrantySettingsForm((prev) => ({ ...prev, [id]: value }));
      }
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      const changedFields = {};
      Object.keys(warrantySettingsForm).forEach((key) => {
        const currentVal = warrantySettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        return;
      }

      const warrantySettings = {
        warrantySettingsData: changedFields,
      };
      const result = await updateWarrantySettings(warrantySettings);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Warranty settings updated successfully!',
        });
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to update warranty settings!',
        });
      }
      findWarrantySettings();
    };

    const onClickCancel = () => {
      navigate('/');
    };

    const handleAddPeriod = () => {
      setEditingPeriod(null);
      setAddPeriodModalOpen(true);
    };

    const handleSavePeriod = async (periodData) => {
      if (editingPeriod) {
        const result = await updateWarrantyPeriod(periodData, editingPeriod.id);
        if (result) {
          showAlert({
            isAlertOpen: true,
            alertType: 'success',
            alertMessage: 'Warranty period updated successfully!',
          });
          findWarrantyPeriods();
        } else {
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage: 'Failed to update warranty period!',
          });
        }
      } else {
        const result = await createWarrantyPeriod(periodData);
        if (result) {
          showAlert({
            isAlertOpen: true,
            alertType: 'success',
            alertMessage: 'Warranty period created successfully!',
          });
          findWarrantyPeriods();
        } else {
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage: 'Failed to create warranty period!',
          });
        }
      }
    };

    const handleUpdatePeriod = (periodId) => {
      const period = warrantyPeriodsData.find((p) => p.id === periodId);
      if (period) {
        const editingPeriodData = {
          ...period,
          unit: period.unit ? period.unit.toLowerCase() : 'year',
        };
        setEditingPeriod(editingPeriodData);
        setAddPeriodModalOpen(true);
      }
    };

    const handleDeletePeriodClick = (periodId) => {
      setPeriodIdToDelete(periodId);
      setDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
      setDeleteConfirmOpen(false);
      setPeriodIdToDelete(null);
    };

    const handleConfirmDeletePeriod = async () => {
      if (periodIdToDelete == null) return;
      const result = await deleteWarrantyPeriod(periodIdToDelete);
      if (result) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'Warranty period deleted successfully!',
        });
        findWarrantyPeriods();
      } else {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Failed to delete warranty period!',
        });
      }
    };

    const handleDeletePeriod = handleDeletePeriodClick;

    const WarrantyPeriodsTable = () => {
      const columns = [
        { id: 'period', label: 'Period', minWidth: 100 },
        { id: 'unit', label: 'Unit', minWidth: 100 },
        { id: 'daysPerUnit', label: 'Days Per Unit', minWidth: 150 },
        { id: 'name', label: 'Name', minWidth: 200 },
        { id: 'description', label: 'Description', minWidth: 300 },
        {
          id: 'options',
          minWidth: 20,
          options: ['Update', 'Delete'],
          table: 'warranty-period',
        },
      ];

      const tableRows = (warrantyPeriodsData || []).map((period) => {
        const unit = period.unit || '';
        const capitalizedUnit = unit.charAt(0).toUpperCase() + unit.slice(1);
        return {
          id: period.id,
          period: period.period,
          unit: capitalizedUnit,
          daysPerUnit: period.daysPerUnit || '',
          name: period.name || '',
          description: period.description || '',
        };
      });

      const handleUpdateClick = (id) => handleUpdatePeriod(id);
      const handleDeleteClick = (id) => handleDeletePeriod(id);

      const tableColumns = columns.map((col) =>
        col.id === 'options'
          ? { ...col, customHandlers: { onUpdate: handleUpdateClick, onDelete: handleDeleteClick } }
          : col
      );

      return (
        <Table
          columns={tableColumns}
          rows={tableRows}
          disableInternalPagination={false}
        />
      );
    };

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Warranty Settings</h1>
            <p className={styles.breadcrumb}>Settings / Warranty</p>
          </div>
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Warranty Period Configurations</h2>
                  <p className={styles.topicCaption}>
                    Here you can change the warranty period configurations
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Input
                    id="days-in-warranty-year"
                    label="Days in a warranty year"
                    type="number"
                    value={warrantySettingsForm['days-in-warranty-year']}
                    onInput={handleChangeInputValues}
                    placeholder="Standard Year"
                  />
                  <Input
                    id="days-in-warranty-month"
                    label="Days in a warranty month"
                    type="number"
                    value={warrantySettingsForm['days-in-warranty-month']}
                    onInput={handleChangeInputValues}
                    placeholder="Standard Month"
                  />
                </div>
                <div className={styles.rowDiv}>
                  <Input
                    id="days-in-warranty-week"
                    label="Days in a warranty week"
                    type="number"
                    value={warrantySettingsForm['days-in-warranty-week']}
                    onInput={handleChangeInputValues}
                    placeholder="Standard Week"
                  />
                </div>

                <div className={styles.line} />

                <div className={styles.topicDiv}>
                  <div className={styles.sectionHeader}>
                    <div>
                      <h2 className={styles.topicLabel}>Warranty Periods</h2>
                      <p className={styles.topicCaption}>
                        Manage your warranty periods here
                      </p>
                    </div>
                    <Button type="button" onClick={handleAddPeriod}>
                      Add Period
                    </Button>
                  </div>
                </div>
                <div className={styles.tableContainer}>
                  <WarrantyPeriodsTable />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={onClickCancel}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
        <AddPeriodModal
          open={isAddPeriodModalOpen}
          setOpen={setAddPeriodModalOpen}
          onSave={handleSavePeriod}
          editingPeriod={editingPeriod}
        />
        <ConfirmModal
          open={deleteConfirmOpen}
          onClose={handleCloseDeleteConfirm}
          onConfirm={handleConfirmDeletePeriod}
          title="Confirm"
          message="Are you sure you want to delete this warranty period?"
          confirmLabel="OK"
          cancelLabel="Cancel"
        />
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  warrantySettingsState: state.warrantySettingsState,
});

export default connect(mapStateToProps, {
  showAlert,
  updateWarrantySettings,
  findWarrantySettings,
  findWarrantyPeriods,
  createWarrantyPeriod,
  updateWarrantyPeriod,
  deleteWarrantyPeriod,
})(WarrantySettings);
